"use client";
// beui.dev/components/motion/expanding-arrow-button
// Restyled for Bavimail: pill on a hairline dark surface, signal-orange accent
// with ink chevrons, Manrope label. Adds `ExpandingArrowLink` because the hero
// CTA navigates; both share one body so they cannot drift apart.

import { motion, useReducedMotion, type HTMLMotionProps } from "motion/react";
import { forwardRef, useState, type ReactNode } from "react";
import { EASE_OUT, SPRING_LAYOUT, SPRING_PRESS } from "@/lib/ease";
import { useHoverCapable } from "@/lib/hooks/use-hover-capable";
import { cn } from "@/lib/utils";

interface ExpandingArrowOwnProps {
  children: ReactNode;
  accentClassName?: string;
  labelClassName?: string;
}

export interface ExpandingArrowButtonProps
  extends Omit<HTMLMotionProps<"button">, "children">, ExpandingArrowOwnProps {}

export interface ExpandingArrowLinkProps
  extends Omit<HTMLMotionProps<"a">, "children">, ExpandingArrowOwnProps {}

const ARROW_OPACITY = [1, 0.78, 0.54, 0.32, 0.16] as const;

// 52px tall with a 5px inset, so the resting accent is a 42px circle.
const INSET = 5;
const ACCENT_SIZE = 42;

function DottedChevron({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 20 28" fill="none" aria-hidden="true" className={className}>
      <circle cx="4" cy="4" r="2" fill="currentColor" />
      <circle cx="10" cy="9" r="2" fill="currentColor" />
      <circle cx="16" cy="14" r="2" fill="currentColor" />
      <circle cx="10" cy="19" r="2" fill="currentColor" />
      <circle cx="4" cy="24" r="2" fill="currentColor" />
    </svg>
  );
}

/** Class list for the shell, for elements that can be neither a button nor a link. */
export function expandingArrowClassName(className?: string) {
  return cn(
    "relative inline-flex h-[52px] min-w-64 items-center overflow-hidden rounded-full select-none",
    "border border-hb-card bg-hb-surface text-hb-heading",
    "transition-colors duration-[var(--motion-duration-fast)] hover:border-hb-border-hover",
    "outline-none focus-visible:ring-2 focus-visible:ring-hb-ring focus-visible:ring-offset-2 focus-visible:ring-offset-hb-page",
    "disabled:pointer-events-none disabled:opacity-40",
    className,
  );
}

/** Hover (on hover-capable devices) or keyboard focus expands the accent. */
function useExpandingArrow(disabled: boolean) {
  const reduce = useReducedMotion() ?? false;
  const canHover = useHoverCapable();
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const active = !disabled && ((canHover && hovered) || focused);
  return { reduce, active, setHovered, setFocused };
}

export function ExpandingArrowBody({
  active,
  reduce,
  children,
  accentClassName,
  labelClassName,
}: ExpandingArrowOwnProps & { active: boolean; reduce: boolean }) {
  return (
    <>
      <motion.span
        layout="size"
        aria-hidden="true"
        transition={reduce ? { duration: 0 } : SPRING_LAYOUT}
        style={{
          top: INSET - 1,
          bottom: INSET - 1,
          left: INSET - 1,
          width: active ? `calc(100% - ${(INSET - 1) * 2}px)` : ACCENT_SIZE,
          borderRadius: 9999,
        }}
        className={cn(
          "absolute z-10 overflow-hidden bg-hb-accent text-hb-accent-fg",
          accentClassName,
        )}
      >
        <motion.span
          animate={{ opacity: active ? 0 : 1 }}
          transition={{ duration: reduce ? 0 : 0.1, ease: EASE_OUT }}
          className="absolute inset-0 grid place-items-center"
        >
          <DottedChevron className="h-[22px] w-4" />
        </motion.span>

        <span className="absolute inset-0 flex items-center justify-around px-3">
          {ARROW_OPACITY.map((opacity, index) => (
            <motion.span
              key={opacity}
              animate={{
                opacity: active ? 1 : 0,
                transform: active && !reduce ? "translateX(0px)" : "translateX(-6px)",
              }}
              transition={{
                duration: reduce ? 0 : 0.18,
                delay: active && !reduce ? 0.04 + index * 0.025 : 0,
                ease: EASE_OUT,
              }}
              // The accent's ink, fading along the run.
              style={{
                color: `color-mix(in oklab, var(--hb-accent-fg) ${Math.round(opacity * 100)}%, transparent)`,
              }}
              className="inline-grid place-items-center"
            >
              <DottedChevron className="h-[22px] w-4" />
            </motion.span>
          ))}
        </span>
      </motion.span>

      <motion.span
        animate={{
          opacity: active ? 0 : 1,
          transform: active && !reduce ? "translateX(6px)" : "translateX(0px)",
        }}
        transition={{ duration: reduce ? 0 : 0.12, ease: EASE_OUT }}
        className={cn(
          "relative z-0 mr-6 ml-[60px] font-hb text-base font-bold whitespace-nowrap",
          labelClassName,
        )}
      >
        {children}
      </motion.span>
    </>
  );
}

export const ExpandingArrowButton = forwardRef<HTMLButtonElement, ExpandingArrowButtonProps>(
  function ExpandingArrowButton(
    {
      children,
      className,
      accentClassName,
      labelClassName,
      disabled,
      onMouseEnter,
      onMouseLeave,
      onFocus,
      onBlur,
      ...rest
    },
    ref,
  ) {
    const { reduce, active, setHovered, setFocused } = useExpandingArrow(Boolean(disabled));

    return (
      <motion.button
        ref={ref}
        type="button"
        disabled={disabled}
        onMouseEnter={(event) => {
          setHovered(true);
          onMouseEnter?.(event);
        }}
        onMouseLeave={(event) => {
          setHovered(false);
          onMouseLeave?.(event);
        }}
        onFocus={(event) => {
          setFocused(true);
          onFocus?.(event);
        }}
        onBlur={(event) => {
          setFocused(false);
          onBlur?.(event);
        }}
        whileTap={reduce || disabled ? undefined : { scale: 0.97 }}
        transition={SPRING_PRESS}
        className={expandingArrowClassName(className)}
        {...rest}
      >
        <ExpandingArrowBody
          active={active}
          reduce={reduce}
          accentClassName={accentClassName}
          labelClassName={labelClassName}
        >
          {children}
        </ExpandingArrowBody>
      </motion.button>
    );
  },
);

export const ExpandingArrowLink = forwardRef<HTMLAnchorElement, ExpandingArrowLinkProps>(
  function ExpandingArrowLink(
    {
      children,
      className,
      accentClassName,
      labelClassName,
      onMouseEnter,
      onMouseLeave,
      onFocus,
      onBlur,
      ...rest
    },
    ref,
  ) {
    const { reduce, active, setHovered, setFocused } = useExpandingArrow(false);

    return (
      <motion.a
        ref={ref}
        onMouseEnter={(event) => {
          setHovered(true);
          onMouseEnter?.(event);
        }}
        onMouseLeave={(event) => {
          setHovered(false);
          onMouseLeave?.(event);
        }}
        onFocus={(event) => {
          setFocused(true);
          onFocus?.(event);
        }}
        onBlur={(event) => {
          setFocused(false);
          onBlur?.(event);
        }}
        whileTap={reduce ? undefined : { scale: 0.97 }}
        transition={SPRING_PRESS}
        className={expandingArrowClassName(className)}
        {...rest}
      >
        <ExpandingArrowBody
          active={active}
          reduce={reduce}
          accentClassName={accentClassName}
          labelClassName={labelClassName}
        >
          {children}
        </ExpandingArrowBody>
      </motion.a>
    );
  },
);
